/**
 * AppointmentDetail — the appointment record as a DEDICATED PAGE (route
 * `appointment.detail?id=…`), replacing the side drawer.
 *
 * 2026-08-06, operator: "die appointment detail sideview ist nicht gut, in
 * calenso ist es eine dedizierte seite" + a Calenso screenshot. The information
 * architecture is modelled on that reference:
 *
 *   · a breadcrumb (Dashboard › Appointments › <customer>) and an ACTION BAR
 *     across the top — the lifecycle verbs, not a kebab;
 *   · a wide left column of record cards — Basic information (incl. the answered
 *     booking questions), Participants, Date & time, Price & payment, Notes;
 *   · a narrow right column, "Appointment metadata" — customer, created,
 *     booking channel, the UTM parameters each with its explaining caption, the
 *     booking client (source URL, IP, browser, OS, engine, device, user agent)
 *     and the customer/admin deep links with copy buttons.
 *
 * WHERE IT DELIBERATELY DIFFERS FROM THE REFERENCE: Calenso's page is an edit
 * form with Save / Discard. There is no `updateAppointment` operation — only
 * reschedule, cancel, check-in and no-show — so this page READS the record and
 * acts through the ops that exist. Fields are values, not inputs that would
 * silently drop what you typed. The gap is filed as REQ-132.
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Btn, Button, Dropdown, Icon, Input, T, Tooltip } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import { Screen, PageHeader } from "../../shell/chrome";
import { Modal } from "../../shell/Overlay";
import { useNav, routeToPath } from "../../shell/router";
import { LoadState } from "../../shell/composites";
import { useIsMobile } from "../../shell/useResponsive";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import {
  addBlock, addAppointmentPayment, cancelAppointment, checkInAppointment, deleteAppointmentPayment,
  getAppointment, listBlocks, markAppointmentNoShow, removeBlock, setAppointmentPrice, updateAppointmentPayment,
  type Appointment, type ApptStatus, type BlockEntry, type BlockKind, OPEN_APPT_STATUSES } from "./appointments-api";
import { RescheduleDrawer } from "./AppointmentsBook";
import { canViewAsCustomer, openManagePreview } from "./manage-preview";
import { getQuestionAnswerFile } from "@v0y4ge/api-client";
import { TimeZoneNote, zoneLabel } from "../../shell/TimeZoneNote";
import { tzContext, wallClockToIso, friendlyTime } from "../../lib/datetime";
import { useStaff } from "../staff/useStaff";
import { useLocations } from "../locations/useLocations";

/* Whose wall clock is this? (2026-08-07, Pat)
 * A booking is STORED as the branch's local date + start/end. The assigned
 * staff member may sit in another zone — that is exactly what the profile
 * default on their record is for — so the record states the zone it is
 * printing and, when the two differ, the same slot in the staff member's own
 * zone. Nothing is silently re-zoned: the big fields keep branch time, which
 * is what the branch, the customer and the room are all on. */
/** One stored answer file. Opening asks getQuestionAnswerFile for a fresh downloadUrl each
 *  time (the URL is short-lived); a file not yet clean, or a read this viewer may not make,
 *  says so instead of opening nothing. The tab is opened before the await so the browser
 *  does not block it as a pop-up. */
export function AnswerFileLink({ fileId, name }: { fileId: string; name: string }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const open = async () => {
    if (busy) return;
    setBusy(true);
    const tab = window.open("", "_blank");
    try {
      const r = await getQuestionAnswerFile(fileId);
      if (r && r.downloadUrl) {
        if (tab) { tab.opener = null; tab.location.href = r.downloadUrl; } else window.location.href = r.downloadUrl;
      } else {
        if (tab) tab.close();
        toast({ tone: "danger", title: t("appointments.detail.fileNotReady", "This file can't be opened yet"), subtitle: r && r.scanStatus === "blocked" ? t("appointments.detail.fileBlocked", "It didn't pass the file checks.") : t("appointments.detail.fileNotChecked", "It hasn't finished its checks.") });
      }
    } catch (e) {
      if (tab) tab.close();
      const err = e as { status?: number; message?: string } | null;
      toast({ tone: "danger", title: err?.status === 403 ? t("appointments.detail.fileForbidden", "You don't have access to this file") : t("appointments.detail.fileOpenFailed", "The file could not be opened"), subtitle: err?.status === 403 ? undefined : err?.message });
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={() => { void open(); }} disabled={busy}
      style={{ background: "none", border: 0, padding: 0, font: "inherit", color: T.p1, textDecoration: "underline", cursor: busy ? "progress" : "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
      <Icon.download size={12} />{name}
    </button>
  );
}

function ApptZoneLine({ appt }: { appt: { date: string; start: string; end: string; locationId: string; staffId: string | null; staffName: string | null; clockTimeZone?: string } }) {
  const t = useT();
  const { members } = useStaff();
  const { locations } = useLocations();
  // The record's own clock zone first (CC 0037); the locations lookup only as a fallback.
  const branchTz = appt.clockTimeZone || locations.find((l) => l.id === appt.locationId)?.tz || undefined;
  const ctx = tzContext(branchTz);
  const staff = members.find((m) => (appt.staffId && m.id === appt.staffId)
    || (!!appt.staffName && (m.name || "").toLowerCase() === appt.staffName.toLowerCase()));
  const staffTz = ((staff as { timezone?: string | null } | undefined)?.timezone) || null;
  const startIso = wallClockToIso(appt.date, appt.start, ctx.zone);
  const endIso = wallClockToIso(appt.date, appt.end, ctx.zone);
  const inStaffZone = staffTz && staffTz !== ctx.zone && startIso
    ? `${friendlyTime(startIso, staffTz)}${endIso ? ` — ${friendlyTime(endIso, staffTz)}` : ""}`
    : null;
  return (
    <TimeZoneNote testid="appt-detail-zone-note">
      {t("appointments.detail.timesBranchLocal", "Times are the branch's local time")} ({zoneLabel(ctx.zone)})
      {inStaffZone ? <> · {appt.staffName || staff?.name}: <b style={{ color: T.n2 }}>{inStaffZone}</b> ({zoneLabel(staffTz as string)})</> : null}
      {ctx.differs ? <> · {t("appointments.detail.deviceZoneDiffers", "your device is on")} {ctx.browserLabel}</> : null}
    </TimeZoneNote>
  );
}

// ui-raw: bespoke 7-way status palette (incl. "rescheduled" purple, which has no
// jrni Tone equivalent) — checked against tokens.css and these do NOT match the
// jrni semantic pairs (jrni red1 #D01647 vs this #B91C1C; blue1 #2D60EB vs
// #1D4ED8; orange1 #916D0F vs #B45309; green2 #E6F4EE vs #EAF7F0) — converting
// to T.* would shift the rendered colors, so this stays a deliberate custom map.
const STATUS_META: Record<ApptStatus, { label: string; labelKey: string; fg: string; bg: string }> = {
  booked:        { label: "Booked", labelKey: "appointments.status.booked.label",       fg: "#4B5563", bg: "#F3F4F6" },
  confirmed:     { label: "Confirmed", labelKey: "appointments.status.confirmed.label",    fg: "#1D4ED8", bg: "#EFF4FF" },
  "checked-in":  { label: "Checked in", labelKey: "appointments.status.checked_in.label",   fg: "#027D47", bg: "#EAF7F0" },
  served:        { label: "Served", labelKey: "appointments.status.served.label",       fg: "#374151", bg: "#EDEFF2" },
  "no-show":     { label: "No-show", labelKey: "appointments.status.no_show.label",      fg: "#B45309", bg: "#FEF4E6" },
  cancelled:     { label: "Cancelled", labelKey: "appointments.status.cancelled.label",    fg: "#B91C1C", bg: "#FDECEC" },
  rescheduled:   { label: "Rescheduled", labelKey: "appointments.status.rescheduled.label",  fg: "#6D28D9", bg: "#F2EEFC" },
};
const CHANNEL_LABEL: Record<string, string> = { inPerson: "In person", phone: "Phone", video: "Video" };
const VIA_LABEL: Record<string, string> = { website: "Website", mobile: "Mobile app", phone: "Phone", email: "Email", staff: "Booked by staff", queue: "From a queue", event: "From an event", journey: "Self-service", import: "Imported" };
/* Shared with the other appointment screen (CC 0254): "rescheduled" counts as open. */
const OPEN_STATUSES = OPEN_APPT_STATUSES;
const CURRENCIES = ["CHF", "EUR", "GBP", "USD"];
/** The payment methods a counter actually takes. Cash first — it is the one a
 *  staff member reaches for when someone pays in the boutique. */
const PAY_METHODS: { value: string; label: string; labelKey?: string }[] = [
  { value: "cash", label: "Cash", labelKey: "appointments.detail.method.cash" },
  { value: "card", label: "Credit card", labelKey: "appointments.detail.method.card" },
  { value: "debit", label: "Debit card", labelKey: "appointments.detail.method.debit" },
  { value: "online", label: "Online payment", labelKey: "appointments.detail.method.online" },
  { value: "twint", label: "TWINT" },
  { value: "paypal", label: "PayPal", labelKey: "appointments.detail.method.paypal" },
  { value: "applepay", label: "Apple Pay", labelKey: "appointments.detail.method.applepay" },
  { value: "banktransfer", label: "Bank transfer", labelKey: "appointments.detail.method.banktransfer" },
  { value: "invoice", label: "Invoice", labelKey: "appointments.detail.method.invoice" },
  { value: "giftcard", label: "Gift card", labelKey: "appointments.detail.method.giftcard" },
  { value: "voucher", label: "Voucher", labelKey: "appointments.detail.method.voucher" },
];
const BLOCK_KIND_LABEL: Record<BlockKind, string> = { customer: "Customer", email: "Email address", phone: "Phone number", ip: "IP address" };
/** Catalog key per block kind; BLOCK_KIND_LABEL stays the English fallback. */
const BLOCK_KIND_LABEL_KEY: Record<BlockKind, string> = { customer: "appointments.detail.blockKind.customer", email: "appointments.detail.blockKind.email", phone: "appointments.detail.blockKind.phone", ip: "appointments.detail.blockKind.ip" };

const card: CSSProperties = { background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", gap: 16 };
const cardTitle: CSSProperties = { margin: 0, fontSize: 15, fontWeight: 700, color: T.n1, display: "flex", alignItems: "center", gap: 9 };
const metaLabel: CSSProperties = { fontSize: 11, fontWeight: 700, color: T.soft, letterSpacing: "0.05em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 7 };
const fieldLabel: CSSProperties = { fontSize: 12.5, color: T.n2, fontWeight: 500 };
/** A read value in the shape of the reference's form field — same rhythm, no fake input. */
const fieldBox: CSSProperties = { minHeight: 40, padding: "9px 12px", border: `1px solid ${T.n5}`, borderRadius: 6, background: T.n6, fontSize: 13.5, color: T.n1, display: "flex", alignItems: "center", gap: 8, wordBreak: "break-word" };

function Field({ label, children, hint, span }: { label: string; children: ReactNode; hint?: string; span?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, gridColumn: span ? "1 / -1" : undefined, minWidth: 0 }}>
      <span style={fieldLabel}>{label}</span>
      <div style={fieldBox}>{children}</div>
      {hint ? <span style={{ fontSize: 11.5, color: T.soft }}>{hint}</span> : null}
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 12, color: T.soft }}>{label}</span>
      <span style={{ fontSize: 13, color: T.n1, fontFamily: mono ? T.fontMono : undefined, wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

function CopyLink({ label, url }: { label: string; url: string }) {
  const t = useT();
  const copy = () => {
    try { void navigator.clipboard.writeText(url); toast({ tone: "success", title: t("appointments.detail.linkCopied", "Link copied"), subtitle: label }); }
    catch { toast({ tone: "danger", title: t("appointments.detail.copyFailed", "Couldn't copy the link") }); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 30 }}>
      <a href={url} style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</a>
      <Tooltip content={t("common.actions.copy", "Copy")}>
        <Btn type="button" variant="ghost" onClick={copy} aria-label={t("appointments.detail.ariaLabel", "{arg1} — {label}", { arg1: t("common.actions.copy", "Copy"), label })}
          iconOnly={<Icon.copy size={14} />}
          style={{ width: 28, height: 28, borderRadius: 6, border: "none", background: "transparent", color: T.soft, boxShadow: "none" }} />
      </Tooltip>
    </div>
  );
}

/* The server's own reason for a refused state change (CC 0266), e.g. "No-show can only be set
   after the appointment start time", instead of the generic line. */
const actionFailTitle = (e: unknown, fallback: string) => {
  const err = e as { code?: string; message?: string } | null;
  return err?.code === "INVALID_STATE" && err.message ? err.message : fallback;
};
/** The appointment's start as an instant on its own clock (CC 0266), or null when its zone or
 *  start is unknown. */
const startInstantMs = (date?: string | null, start?: string | null, zone?: string | null): number | null => {
  if (!date || !start || !zone) return null;
  const iso = wallClockToIso(date, String(start).slice(0, 5), zone);
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
};
/** Whether the appointment's start has passed on its own clock (CC 0266). The server refuses
 *  No-show before it. An unknown zone or start keeps No-show hidden. */
const startHasPassed = (date?: string | null, start?: string | null, zone?: string | null) => {
  const ms = startInstantMs(date, start, zone);
  return ms !== null && Date.now() >= ms;
};
/** setTimeout's longest delay (about 24.8 days); a start further out re-arms after it. */
const MAX_TIMER_MS = 2_147_483_647;

export default function AppointmentDetail() {
  const t = useT();
  const nav = useNav();
  const isMobile = useIsMobile();
  const id = String((nav.params as Record<string, unknown>).id ?? "");
  const canBook = can("appointments.book");
  const canCheckin = can("appointments.checkin");
  /* The branch zone, for "has the start passed" when the record carries no clockTimeZone (CC 0266). */
  const { locations: zoneLocations } = useLocations();
  const [previewBusy, setPreviewBusy] = useState(false);
  const [appt, setAppt] = useState<Appointment | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resched, setResched] = useState(false);
  const [blocks, setBlocks] = useState<BlockEntry[]>([]);
  const [blockDraft, setBlockDraft] = useState<{ kind: BlockKind; value: string; label: string } | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [priceDraft, setPriceDraft] = useState<string>("");
  const [currency, setCurrency] = useState("CHF");
  // Collapsed by default (operator, 2026-08-06): the card shows the money line
  // and nothing else until someone actually needs to settle the charge.
  const [payOpen, setPayOpen] = useState(false);
  /* Cancelling emails the customer and cannot be undone, so it asks first (CC 0164). */
  const [cancelAsk, setCancelAsk] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const load = useCallback(() => {
    if (!id) { setErr("No appointment id in the URL"); return; }
    getAppointment(id).then((a) => { setAppt(a); setErr(null); }).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const loadBlocks = useCallback(() => { listBlocks().then((r) => setBlocks(r.entries || [])).catch(() => { /* block list unavailable — the buttons stay, the chips do not lie */ }); }, []);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);
  useEffect(() => {
    if (!appt) return;
    setPriceDraft(appt.price ? String(appt.price.amount) : "");
    setCurrency(appt.price?.currency || "CHF");
  }, [appt?.id, appt?.price?.amount, appt?.price?.currency]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Block list ── */
  const blockOf = (kind: BlockKind, value?: string | null) =>
    value ? blocks.find((b) => b.kind === kind && b.value.toLowerCase() === String(value).toLowerCase()) ?? null : null;
  const confirmBlock = async () => {
    if (!blockDraft) return;
    setBusy(true);
    try {
      await addBlock({ kind: blockDraft.kind, value: blockDraft.value, label: blockDraft.label, reason: blockReason.trim() || undefined });
      toast({ tone: "success", title: t("appointments.detail.blocked", "Blocked"), subtitle: t("appointments.detail.blockEntry", "{kind} · {value}", { kind: t(BLOCK_KIND_LABEL_KEY[blockDraft.kind], BLOCK_KIND_LABEL[blockDraft.kind]), value: blockDraft.value }) });
      setBlockDraft(null); setBlockReason(""); loadBlocks();
    } catch (e) { toast({ tone: "danger", title: t("appointments.detail.blockFailed", "Couldn't block this"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  const unblock = async (entry: BlockEntry) => {
    setBusy(true);
    try { await removeBlock(entry.id); toast({ tone: "success", title: t("appointments.detail.unblocked", "Unblocked"), subtitle: t("appointments.detail.blockEntry", "{kind} · {value}", { kind: t(BLOCK_KIND_LABEL_KEY[entry.kind], BLOCK_KIND_LABEL[entry.kind]), value: entry.value }) }); loadBlocks(); }
    catch (e) { toast({ tone: "danger", title: t("appointments.detail.unblockFailed", "Couldn't unblock this"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };

  /* ── Price & payments ── */
  const payments = appt?.payments ?? [];
  const total = appt?.price?.amount ?? 0;
  const paidSum = payments.filter((p) => p.paid).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const outstanding = Math.max(0, total - paidSum);
  const payState = total <= 0 ? "none" : paidSum <= 0 ? "unpaid" : outstanding > 0 ? "partial" : "paid";
  const money = (n: number) => `${currency} ${n.toFixed(2)}`;
  const savePrice = async (amountStr: string, cur = currency) => {
    if (!appt) return;
    const amount = Number(amountStr.replace(",", "."));
    if (!isFinite(amount) || amount < 0) { toast({ tone: "danger", title: t("appointments.detail.priceInvalid", "Price must be a number of zero or more") }); return; }
    if (appt.price && appt.price.amount === amount && appt.price.currency === cur) return;
    setBusy(true);
    try { setAppt(await setAppointmentPrice(appt.id, { amount, currency: cur })); toast({ tone: "success", title: t("appointments.detail.priceSaved", "Price saved") }); }
    catch (e) { toast({ tone: "danger", title: t("appointments.detail.priceFailed", "Couldn't save the price"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  const payAct = async (fn: () => Promise<Appointment>, msg: string) => {
    setBusy(true);
    try { setAppt(await fn()); toast({ tone: "success", title: msg }); }
    catch (e) { toast({ tone: "danger", title: t("appointments.detail.paymentFailed", "Couldn't update the payment"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };

  /** Block / unblock control for one identifier. Same shape everywhere it appears. */
  const blockControl = (kind: BlockKind, value?: string | null, label?: string) => {
    if (!value) return null;
    const hit = blockOf(kind, value);
    return hit ? (
      <Btn type="button" variant="secondary" disabled={busy} onClick={() => { void unblock(hit); }} data-testid={`appt-unblock-${kind}`}
        style={{ gap: 6, height: 30, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, color: T.n1, fontSize: 12, fontWeight: 600, boxShadow: "none" }}>
        <Icon.check size={12} /> {t("appointments.detail.unblock", "Unblock")}
      </Btn>
    ) : (
      <Btn type="button" variant="secondary" disabled={busy} onClick={() => { setBlockDraft({ kind, value, label: label || value }); setBlockReason(""); }} data-testid={`appt-block-${kind}`}
        style={{ gap: 6, height: 30, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.red1}`, background: T.white, color: T.red1, fontSize: 12, fontWeight: 600, boxShadow: "none" }}>
        <Icon.x size={12} /> {t("appointments.detail.block", "Block")}
      </Btn>
    );
  };
  const activeBlocks = appt ? ([
    blockOf("customer", appt.customer.name), blockOf("email", appt.customer.email),
    blockOf("phone", appt.customer.phone), blockOf("ip", appt.client?.ip),
  ].filter(Boolean) as BlockEntry[]) : [];

  const act = async (fn: () => Promise<Appointment>, okMsg: string): Promise<boolean> => {
    setBusy(true);
    try { const next = await fn(); setAppt(next); toast({ tone: "success", title: okMsg }); return true; }
    catch (e) { const fb = t("appointments.book.actionFailed", "Couldn't update the appointment"); const title = actionFailTitle(e, fb); toast({ tone: "danger", title, subtitle: title === fb ? (e instanceof Error ? e.message : String(e)) : undefined }); return false; }
    finally { setBusy(false); }
  };

  const viewAsCustomer = async () => {
    if (!appt || previewBusy) return;
    setPreviewBusy(true);
    try {
      const r = await openManagePreview(appt.id);
      if (r === "unavailable") toast({ tone: "warn", title: "The customer's page isn't available on this workspace" });
      else if (r === "error") toast({ tone: "warn", title: "Couldn't open the customer's page", subtitle: "Try again." });
    } finally { setPreviewBusy(false); }
  };

  const st = appt ? STATUS_META[appt.status] : null;
  const open = !!appt && OPEN_STATUSES.includes(appt.status);
  /* No-show appears the moment the start passes (CC 0271): while the appointment is open and its
     start is ahead, one timer for that instant (plus a second) re-renders the screen. */
  const noShowZone = appt ? (appt.clockTimeZone || zoneLocations.find((l) => l.id === appt.locationId)?.tz) : null;
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    if (!appt || !open) return;
    const startMs = startInstantMs(appt.date, appt.start, noShowZone);
    if (startMs === null || Date.now() >= startMs) return;
    const handle = setTimeout(() => setClockTick((n) => n + 1), Math.min(startMs - Date.now() + 1000, MAX_TIMER_MS));
    return () => clearTimeout(handle);
  }, [appt?.id, appt?.date, appt?.start, noShowZone, open, clockTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const at = appt?.attribution;
  const cl = appt?.client;
  const manageUrl = (action?: string) => {
    const path = routeToPath("appointment.manage", { ref: appt?.ref, ...(action ? { action } : {}) });
    try { return window.location.origin + path; } catch { return path; }
  };

  return (
    <Screen rail="appt-book">
      <PageHeader
        title={appt ? appt.customer.name : t("appointments.detail.appointment", "Appointment")}
        subtitle={appt ? t("appointments.detail.subtitle", "{service} · {date} {start}–{end}", { service: appt.service, date: appt.date, start: appt.start, end: appt.end }) : undefined}
        breadcrumb={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("appointments")}>
              <Icon.arrow dir="left" size={11} /> {t("appointments.book.appointments", "Appointments")}
            </span>
            <span style={{ color: T.n4 }}>/</span>
            <span style={{ color: T.n2 }}>{appt ? appt.customer.name : t("appointments.detail.appointment", "Appointment")}</span>
          </span>
        }
        actions={<span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Offered on any status, cancelled included, since that is a common reason to look. */}
          {appt && canViewAsCustomer() ? (
            <Btn type="button" variant="secondary" size="sm" disabled={previewBusy} data-testid="appt-detail-preview"
              onClick={() => { void viewAsCustomer(); }}>
              <Icon.external size={13} /> {previewBusy ? "Opening…" : "View as customer"}
            </Btn>
          ) : null}
          {st ? <span style={{ padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, color: st.fg, background: st.bg }}>{t(st.labelKey, st.label)}</span> : null}
        </span>}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <LoadState loading={!appt && !err} error={err} onRetry={load} testid="appt-detail"
          noun={t("appointments.detail.appointmentNoun", "appointment")}
          loadingLabel={t("appointments.detail.loading", "Loading the appointment…")}>
          {appt ? (
            <div style={{ padding: isMobile ? "0 16px 32px" : "0 24px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
              {activeBlocks.length ? (
                /* ui-raw: same bespoke non-token status palette as STATUS_META above
                   (#FDECEC/#B91C1C match the "cancelled" pair exactly) — Banner's
                   tone="danger" resolves from toneColors()/T.red1, which the STATUS_META
                   comment already proved does NOT match these hexes, so adopting Banner
                   here would shift the rendered color; kept raw for pixel parity. */
                <div role="status" data-testid="appt-blocked-banner"
                  style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 16px", borderRadius: 10, background: "#FDECEC", border: "1px solid #F5B5B5" }}>
                  <Icon.warn size={16} style={{ color: "#B91C1C", flexShrink: 0, marginTop: 1 }} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#8A1F1F", lineHeight: 1.55 }}>
                    <strong>{t("appointments.detail.blockedTitle", "Blocked from booking")}</strong>
                    <div style={{ marginTop: 3 }}>
                      {activeBlocks.map((b) => t("appointments.detail.activeBlockEntry", "{kind}: {value}", { kind: t(BLOCK_KIND_LABEL_KEY[b.kind], BLOCK_KIND_LABEL[b.kind]), value: b.value })).join(" · ")}
                      {activeBlocks.find((b) => b.reason) ? t("appointments.detail.text3", "— {reason}", { reason: activeBlocks.find((b) => b.reason)!.reason }) : ""}
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Action bar — the lifecycle verbs, out in the open (reference: the
                  Umbuchen / Nicht erschienen / Stornieren row). A settled
                  appointment has no verbs left, so the bar is not rendered at all
                  rather than painted empty. */}
              {open || appt.status === "checked-in" ? (
              <div style={{ ...card, padding: 12, flexDirection: "row", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {open && canBook ? (
                  <Button variant="secondary" disabled={busy} onClick={() => setResched(true)} data-testid="appt-detail-resched">
                    <Icon.refresh size={14} /> {t("appointments.book.reschedule", "Reschedule")}
                  </Button>
                ) : null}
                {open && canCheckin && startHasPassed(appt.date, appt.start, noShowZone) ? (
                  <Button variant="secondary" disabled={busy} onClick={() => { void act(() => markAppointmentNoShow(appt.id), t("appointments.book.noShowToast", "Recorded no-show")); }} data-testid="appt-detail-noshow">
                    <Icon.user size={14} /> {t("appointments.book.noShow", "No-show")}
                  </Button>
                ) : null}
                {open && canBook ? (
                  <Btn type="button" variant="secondary" disabled={busy} data-testid="appt-detail-cancel"
                    onClick={() => { setCancelReason(""); setCancelAsk(true); }}
                    style={{ gap: 7, height: 40, padding: "0 14px", borderRadius: 8, border: `1px solid ${T.red1}`, background: T.white, color: T.red1, fontSize: 13.5, fontWeight: 600, boxShadow: "none", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                    <Icon.x size={14} /> {t("appointments.detail.cancelAppointment", "Cancel appointment")}
                  </Btn>
                ) : null}
                <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, color: T.soft, fontFamily: T.fontMono }}>{appt.ref}</span>
                  {open && canCheckin ? (
                    <Button disabled={busy} onClick={() => { void act(() => checkInAppointment(appt.id), t("appointments.book.checkedInToast", "Checked in")); }} data-testid="appt-detail-checkin">
                      <Icon.check size={14} /> {t("appointments.book.checkIn", "Check in")}
                    </Button>
                  ) : appt.status === "checked-in" && canCheckin ? (
                    <Button disabled={busy} onClick={() => { void act(() => checkInAppointment(appt.id), t("appointments.book.servedToast", "Marked served")); }} data-testid="appt-detail-served">
                      <Icon.check size={14} /> {t("appointments.book.markServed", "Mark served")}
                    </Button>
                  ) : null}
                </span>
              </div>
              ) : null}

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 372px", gap: 16, alignItems: "start" }}>
                {/* ── Left: the record ─────────────────────────────────── */}
                <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
                  <section style={card}>
                    <h2 style={cardTitle}><Icon.list size={15} />{t("appointments.detail.basicInformation", "Basic information")}</h2>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                      <Field label={t("appointments.detail.customerName", "Customer")} span><span style={{ fontWeight: 600 }}>{appt.customer.name}</span></Field>
                      <Field label={t("appointments.detail.services", "Services")} span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 6, background: T.p4, color: T.p1, fontSize: 12.5, fontWeight: 600 }}>
                          {appt.service} ({appt.durationMin ?? "—"} {t("appointments.detail.text4", "min)")}</span>
                      </Field>
                      <Field label={t("appointments.detail.duration", "Duration (minutes)")}>{appt.durationMin ?? "—"}</Field>
                      <Field label={t("appointments.detail.category", "Category")}>{appt.channel ? (CHANNEL_LABEL[appt.channel] ?? appt.channel) : t("appointments.detail.channelNotRecorded", "Not recorded")}</Field>
                    </div>

                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                      <h3 style={{ ...cardTitle, fontSize: 13.5 }}>{t("appointments.book.bookingQuestions", "Booking questions")}</h3>
                      {(appt.answers?.length ?? 0) === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: T.soft }}>{t("appointments.book.noAnswers", "No booking questions were answered for this appointment.")}</p>
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                          {appt.answers!.map((a, i) => (
                            <Field key={(a.qid || a.question) + i} label={a.question}>
                              {/* A file answer's names open the file (CC, 28 Sept): getQuestionAnswerFile → downloadUrl. */}
                              {a.files && a.files.length ? (
                                <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 10 }}>
                                  {a.files.map((f) => <AnswerFileLink key={f.fileId} fileId={f.fileId} name={f.name} />)}
                                </span>
                              ) : a.answer}
                            </Field>
                          ))}
                        </div>
                      )}
                    </div>
                  </section>

                  <section style={card}>
                    <h2 style={cardTitle}><Icon.users size={15} />{t("appointments.detail.participants", "Participants")}</h2>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                      <Field label={t("appointments.detail.resourceStaff", "Staff")}>
                        <span style={{ width: 24, height: 24, borderRadius: 999, background: T.p4, color: T.p1, fontSize: 10.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {(appt.staffName ?? "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </span>
                        {appt.staffName ?? t("appointments.book.anyStaff", "Any staff")}
                      </Field>
                      <Field label={t("appointments.detail.customer", "Customer")}>
                        {appt.customer.name}{appt.customer.email ? t("appointments.detail.text5", "({email})", { email: appt.customer.email }) : ""}
                      </Field>
                      <Field label={t("appointments.detail.where", "Where does the appointment take place?")} span>
                        {/* No recorded method: the location alone (CC 0044). */}{appt.channel ? <>{CHANNEL_LABEL[appt.channel] ?? appt.channel} · </> : null}{appt.locationName ?? appt.locationId}
                      </Field>
                      {/* The video meeting's state for staff (CC 0136). */}
                      {(() => {
                        const m = (appt as { meeting?: { status?: string; provider?: string | null; join_url?: string | null; note?: string | null } }).meeting;
                        if (!m) return null;
                        const prov = m.provider === "msteams" || m.provider === "teams" ? "Teams" : (m.provider || "Video");
                        const body = m.status === "ready"
                          ? (m.join_url ? <a href={m.join_url} target="_blank" rel="noopener noreferrer">Join the {prov} meeting</a> : `${prov} meeting created.`)
                          : m.status === "pending" ? `The ${prov} meeting is being created.`
                          : m.status === "failed" ? `The ${prov} meeting could not be created.`
                          : m.note === "staff_has_no_microsoft_account" ? "No Teams meeting: this staff member has no connected Microsoft calendar."
                          : `No ${prov} meeting was created.`;
                        return <Field label="Video meeting" span>{body}</Field>;
                      })()}
                      {appt.attendees?.length ? (
                        <Field label={t("appointments.detail.additionalAttendees", "Additional attendees")} span>
                          {appt.attendees.map((x) => x.name).join(", ")}
                        </Field>
                      ) : null}
                      {appt.additionalStaff?.length ? (
                        <Field label={t("appointments.detail.additionalStaff", "Additional staff")} span>
                          {appt.additionalStaff.map((x) => x.name).join(", ")}
                        </Field>
                      ) : null}
                      {appt.resources?.length ? (
                        <Field label={t("appointments.detail.resources", "Resources")} span>
                          {appt.resources.map((x) => x.name).join(", ")}
                        </Field>
                      ) : null}
                    </div>
                  </section>

                  <section style={card}>
                    <h2 style={cardTitle}><Icon.clock size={15} />{t("appointments.detail.dateAndTime", "Date & time")}</h2>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                      <Field label={t("appointments.detail.start", "Start date & time")}><Icon.calendar size={13} />{appt.date} {appt.start}</Field>
                      <Field label={t("appointments.detail.end", "End date & time")}><Icon.calendar size={13} />{appt.date} {appt.end}</Field>
                      <Field label={t("appointments.detail.bookedOn", "Booked on")} span>
                        {appt.bookedAt ? String(appt.bookedAt).replace("T", " ").slice(0, 16) : "—"}
                      </Field>
                    </div>
                    <ApptZoneLine appt={appt} />
                  </section>

                  {/* Price & payment (2026-08-06): the price is SET here and the
                      charge is settled in parts — each payment is its own record
                      with its own method, so "partially paid" is a computed truth,
                      not a flag someone remembered to tick. */}
                  <section style={card}>
                    <h2 style={{ ...cardTitle, justifyContent: "space-between" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}><Icon.money size={15} />{t("appointments.detail.priceAndPayment", "Price & payment")}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                      {/* ui-raw: same bespoke status-color set as STATUS_META above (paid/
                          partial/unpaid pairs), same verified non-match against T.* */}
                      <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                        color: payState === "paid" ? "#027D47" : payState === "partial" ? "#B45309" : payState === "unpaid" ? "#B91C1C" : T.soft,
                        background: payState === "paid" ? "#EAF7F0" : payState === "partial" ? "#FEF4E6" : payState === "unpaid" ? "#FDECEC" : T.n6 }}>
                        {payState === "paid" ? t("appointments.detail.paid", "Paid")
                          : payState === "partial" ? t("appointments.detail.partiallyPaid", "Partially paid")
                          : payState === "unpaid" ? t("appointments.detail.notPaid", "Not paid")
                          : t("appointments.detail.noCharge", "No charge")}
                      </span>
                      <Btn type="button" variant="link" onClick={() => setPayOpen((v) => !v)} data-testid="appt-price-toggle" aria-expanded={payOpen}
                        style={{ height: "auto", display: "inline-flex", alignItems: "center", gap: 5, padding: 0, color: T.p1, fontSize: 12.5, fontWeight: 600 }}>
                        {payOpen ? t("appointments.detail.showLess", "Show less") : t("appointments.detail.showMore", "Show more")}
                        <Icon.chev size={12} dir={payOpen ? "up" : "down"} />
                      </Btn>
                      </span>
                    </h2>

                    {/* Collapsed: the money line only. */}
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 20, fontWeight: 750, color: T.n1 }}>{money(total)}</span>
                      {total > 0 ? (
                        <span style={{ fontSize: 12.5, color: T.soft }}>
                          {t("appointments.detail.totalPaid", "Total paid")} {money(paidSum)}
                          {outstanding > 0 ? " \u00b7 " + t("appointments.detail.outstanding", "Outstanding") + " " + money(outstanding) : ""}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.detail.setPriceHint", "No price set \u2014 open to set one")}</span>
                      )}
                    </div>

                    {payOpen ? (<>

                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "200px 1fr", gap: 14 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <span style={fieldLabel}>{t("appointments.detail.currency", "Currency")}</span>
                        <Dropdown aria-label={t("appointments.detail.currency", "Currency")} testid="appt-price-currency"
                          value={currency} onChange={(v) => { setCurrency(v); void savePrice(priceDraft || "0", v); }}
                          options={CURRENCIES.map((c) => ({ value: c, label: c }))} />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <span style={fieldLabel}>{t("appointments.detail.price", "Price")} ({currency})</span>
                        <Input type="number" testid="appt-price-amount" value={priceDraft} disabled={!canBook}
                          onChange={(e) => setPriceDraft(e.target.value)}
                          onBlur={() => { if (priceDraft !== "") void savePrice(priceDraft); }}
                          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                          placeholder="0.00" />
                      </div>
                    </div>

                    <div style={{ borderRadius: 8, background: T.n6, border: `1px solid ${T.border}`, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: T.n2 }}>{t("appointments.detail.total", "Total price")}</span>
                        <span style={{ fontWeight: 700, color: T.n1 }}>{money(total)}</span>
                      </div>
                      {/* ui-raw: same bespoke green/orange from the status palette note above */}
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: T.n2 }}>{t("appointments.detail.totalPaid", "Total paid")}</span>
                        <span style={{ fontWeight: 700, color: "#027D47" }}>{money(paidSum)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${T.border}`, paddingTop: 6 }}>
                        <span style={{ color: T.n2 }}>{t("appointments.detail.outstanding", "Outstanding")}</span>
                        <span style={{ fontWeight: 700, color: outstanding > 0 ? "#B45309" : T.n1 }}>{money(outstanding)}</span>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <h3 style={{ ...cardTitle, fontSize: 13.5 }}><Icon.list size={14} />{t("appointments.detail.paymentRecords", "Payment records")}</h3>
                      {canBook ? (
                        <Button variant="secondary" size="sm" disabled={busy} data-testid="appt-payment-add"
                          onClick={() => { void payAct(() => addAppointmentPayment(appt.id, { amount: outstanding > 0 ? outstanding : total, date: new Date().toISOString().slice(0, 10), method: "cash", paid: true }), t("appointments.detail.paymentAdded", "Payment recorded")); }}>
                          <Icon.plus size={13} /> {t("appointments.detail.addPayment", "Add payment")}
                        </Button>
                      ) : null}
                    </div>

                    {payments.length === 0 ? (
                      <p style={{ margin: 0, fontSize: 13, color: T.soft }}>{t("appointments.detail.noPayments", "Nothing has been paid against this appointment yet.")}</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {payments.map((p) => (
                          <div key={p.id} data-testid="appt-payment"
                            style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "128px 1fr 1fr 1fr 36px", gap: 10, alignItems: "center", padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 8 }}>
                            {/* ui-raw: bespoke pill button with an embedded miniature toggle-track
                                graphic — not a plain labeled button (Btn) or a real Toggle control,
                                and the fill colors are the same non-token status palette noted above */}
                            <button type="button" disabled={!canBook || busy}
                              onClick={() => { void payAct(() => updateAppointmentPayment(appt.id, p.id, { paid: !p.paid }), p.paid ? t("appointments.detail.markedPending", "Marked pending") : t("appointments.detail.markedPaid", "Marked paid")); }}
                              style={{ display: "inline-flex", alignItems: "center", gap: 7, minHeight: 30, padding: "0 10px", borderRadius: 999, border: "none", cursor: canBook ? "pointer" : "default", fontFamily: T.font, fontSize: 11.5, fontWeight: 700,
                                background: p.paid ? "#EAF7F0" : T.n6, color: p.paid ? "#027D47" : T.soft }}>
                              <span style={{ width: 26, height: 15, borderRadius: 999, background: p.paid ? "#027D47" : T.n4, position: "relative", flexShrink: 0 }}>
                                <span style={{ position: "absolute", top: 2, left: p.paid ? 13 : 2, width: 11, height: 11, borderRadius: 999, background: T.white }} />
                              </span>
                              {p.paid ? t("appointments.detail.paid", "Paid") : t("appointments.detail.pending", "Pending")}
                            </button>
                            <Input type="number" size="sm" defaultValue={String(p.amount)} disabled={!canBook}
                              onBlur={(e) => { const v = Number(e.target.value); if (isFinite(v) && v > 0 && v !== p.amount) void payAct(() => updateAppointmentPayment(appt.id, p.id, { amount: v }), t("appointments.detail.paymentUpdated", "Payment updated")); }} />
                            <Input type="date" size="sm" defaultValue={p.date} disabled={!canBook}
                              onBlur={(e) => { if (e.target.value && e.target.value !== p.date) void payAct(() => updateAppointmentPayment(appt.id, p.id, { date: e.target.value }), t("appointments.detail.paymentUpdated", "Payment updated")); }} />
                            <Dropdown aria-label={t("appointments.detail.method", "Payment method")} value={p.method} disabled={!canBook}
                              onChange={(v) => { void payAct(() => updateAppointmentPayment(appt.id, p.id, { method: v }), t("appointments.detail.paymentUpdated", "Payment updated")); }}
                              options={PAY_METHODS.map((m) => ({ value: m.value, label: m.labelKey ? t(m.labelKey, m.label) : m.label }))} />
                            {canBook ? (
                              <Tooltip content={t("appointments.detail.deletePayment", "Delete payment")}>
                                <Btn type="button" variant="ghost" disabled={busy} aria-label={t("appointments.detail.deletePayment", "Delete payment")}
                                  iconOnly={<Icon.trash size={15} />}
                                  onClick={() => { void payAct(() => deleteAppointmentPayment(appt.id, p.id), t("appointments.detail.paymentDeleted", "Payment deleted")); }}
                                  style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent", color: T.red1, boxShadow: "none", justifySelf: "end" }} />
                              </Tooltip>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                    </>) : null}
                  </section>

                  {appt.notes ? (
                    <section style={card}>
                      <h2 style={cardTitle}><Icon.edit size={15} />{t("appointments.book.notes", "Notes")}</h2>
                      <p style={{ margin: 0, fontSize: 13.5, color: T.n1, lineHeight: 1.6, textWrap: "pretty" }}>{appt.notes}</p>
                    </section>
                  ) : null}
                </div>

                {/* ── Right: metadata rail ─────────────────────────────── */}
                <aside style={{ ...card, gap: 20, position: isMobile ? "static" : "sticky", top: 0 }}>
                  <h2 style={cardTitle}><Icon.info size={15} />{t("appointments.detail.metadata", "Appointment metadata")}</h2>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span style={metaLabel}><Icon.user size={12} />{t("appointments.detail.customer", "Customer")}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: T.p1 }}>{appt.customer.name}</span>
                      {blockControl("customer", appt.customer.name)}
                    </div>
                    {appt.customer.email ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                        <a href={`mailto:${appt.customer.email}`} style={{ fontSize: 12.5, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{appt.customer.email}</a>
                        {blockControl("email", appt.customer.email)}
                      </div>
                    ) : null}
                    {appt.customer.phone ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                        <span style={{ fontSize: 12.5, color: T.n2 }}>{appt.customer.phone}</span>
                        {blockControl("phone", appt.customer.phone)}
                      </div>
                    ) : null}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                    <MetaRow label={t("appointments.book.reference", "Reference")} value={appt.ref} mono />
                    <MetaRow label={t("appointments.detail.created", "Created")} value={appt.bookedAt ? String(appt.bookedAt).replace("T", " ").slice(0, 16) : "—"} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={metaLabel}><Icon.browser size={12} />{t("appointments.detail.bookingChannel", "Booking channel")}</span>
                      <div style={fieldBox}>{VIA_LABEL[appt.bookedVia || appt.origin || "staff"] ?? "—"}</div>
                      {appt.bookedFrom ? <span style={{ fontSize: 11.5, color: T.soft }}>{appt.bookedFrom}</span> : null}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                    <span style={metaLabel}><Icon.link size={12} />{t("appointments.detail.utmParameters", "UTM parameters")}</span>
                    {at ? (
                      <>
                        <Field label={t("appointments.detail.utmSourceLabel", "Source")} hint={t("appointments.detail.utmSourceHint", "Where the traffic came from (referrer, site name)")}>{at.source || "—"}</Field>
                        <Field label={t("appointments.detail.utmMediumLabel", "Medium")} hint={t("appointments.detail.utmMediumHint", "Marketing medium (CPC, banner, email, social)")}>{at.medium || "—"}</Field>
                        <Field label={t("appointments.detail.utmCampaignLabel", "Campaign")} hint={t("appointments.detail.utmCampaignHint", "Name of the specific campaign or promotion code")}>{at.campaign || "—"}</Field>
                        <Field label={t("appointments.detail.utmTermLabel", "Term")} hint={t("appointments.detail.utmTermHint", "Paid search terms or ad variant")}>{at.term || "—"}</Field>
                        <Field label={t("appointments.detail.utmContentLabel", "Content")} hint={t("appointments.detail.utmContentHint", "Distinguishes ads or links pointing at the same URL")}>{at.content || "—"}</Field>
                        <Field label={t("appointments.detail.utmIdLabel", "ID")} hint={t("appointments.detail.utmIdHint", "A unique campaign id for tracking in analytics")}>{at.id || "—"}</Field>
                      </>
                    ) : (
                      <p style={{ margin: 0, fontSize: 12.5, color: T.soft, lineHeight: 1.55 }}>
                        {/* The "taken by a person" sentence only when a person took it (CC 0030 item 18). */}
                        {["website", "mobile", "journey"].includes(String(appt.bookedVia || appt.origin || ""))
                          ? t("appointments.book.noAttributionSelfService", "No campaign attribution was recorded for this booking.")
                          : t("appointments.book.noAttribution", "No campaign attribution — this booking was taken by a person, not through a self-service link.")}
                      </p>
                    )}
                  </div>

                  {at || cl ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                      <span style={metaLabel}><Icon.globe size={12} />{t("appointments.detail.bookingDetails", "Booking details")}</span>
                      {at?.landing ? <MetaRow label={t("appointments.detail.sourceUrl", "Source")} value={<a href={at.landing}>{at.landing}</a>} /> : null}
                      {at?.referrer ? <MetaRow label={t("appointments.book.referrer", "Referrer")} value={at.referrer} /> : null}
                      {cl?.ip ? (
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, justifyContent: "space-between" }}>
                          <MetaRow label={t("appointments.detail.ipAddress", "IP address")} value={cl.ip} mono />
                          {blockControl("ip", cl.ip)}
                        </div>
                      ) : null}
                      {cl?.browser ? <MetaRow label={t("appointments.detail.browser", "Browser")} value={cl.browser} /> : null}
                      {cl?.os ? <MetaRow label={t("appointments.detail.os", "Operating system")} value={cl.os} /> : null}
                      {cl?.engine ? <MetaRow label={t("appointments.detail.engine", "Engine")} value={cl.engine} /> : null}
                      {cl?.device || at?.device ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, color: T.soft }}>{t("appointments.book.device", "Device")}</span>
                          <span style={{ alignSelf: "flex-start", padding: "3px 10px", borderRadius: 6, background: T.n6, border: `1px solid ${T.border}`, fontSize: 12, fontWeight: 600, color: T.n1 }}>{cl?.device || at?.device}</span>
                        </div>
                      ) : null}
                      {cl?.userAgent ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, color: T.soft }}>{t("appointments.detail.userAgent", "User agent")}</span>
                          <span style={{ padding: "8px 10px", borderRadius: 6, background: T.n6, border: `1px solid ${T.border}`, fontSize: 11, color: T.n2, fontFamily: T.fontMono, lineHeight: 1.5, wordBreak: "break-all" }}>{cl.userAgent}</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div style={{ display: "flex", flexDirection: "column", gap: 6, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                    <span style={metaLabel}><Icon.external size={12} />{t("appointments.detail.appointmentLinks", "Appointment links")}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.p1, marginTop: 4 }}>{t("appointments.detail.customerLinks", "Customer links")}</span>
                    <CopyLink label={t("appointments.detail.linkManage", "Manage booking")} url={manageUrl()} />
                    <CopyLink label={t("appointments.detail.linkCancel", "Deep link: cancel")} url={manageUrl("cancel")} />
                    <CopyLink label={t("appointments.detail.linkReschedule", "Deep link: reschedule")} url={manageUrl("reschedule")} />
                  </div>
                </aside>
              </div>
            </div>
          ) : null}
        </LoadState>
      </div>
      {appt && resched ? (
        <RescheduleDrawer appt={appt} onClose={() => setResched(false)}
          onDone={(a) => { setResched(false); setAppt(a); toast({ tone: "success", title: t("appointments.book.rescheduledToast", "Rescheduled"), subtitle: `${a.date} · ${a.start}` }); }} />
      ) : null}
      {appt && cancelAsk ? (
        <Modal tone="danger" testid="appt-cancel-modal" icon={<Icon.warn size={16} />}
          title="Cancel this booking?"
          sub="This can't be undone. The customer is emailed that the appointment is cancelled."
          onClose={() => { if (!busy) setCancelAsk(false); }}
          footer={<>
            <Button variant="secondary" disabled={busy} onClick={() => setCancelAsk(false)} data-testid="appt-cancel-keep">Keep appointment</Button>
            <Button disabled={busy} data-testid="appt-cancel-confirm"
              onClick={() => { const r = cancelReason.trim(); void act(() => cancelAppointment(appt.id, r || undefined), t("appointments.book.cancelledToast", "Appointment cancelled")).then((ok) => { if (ok) setCancelAsk(false); }); }}>Yes, cancel</Button>
          </>}>
          <Input label="Reason (optional)" value={cancelReason} maxLength={500}
            onChange={(e) => setCancelReason(e.target.value)} testid="appt-cancel-reason"
            placeholder="Recorded on the appointment, e.g. customer called to cancel" />
        </Modal>
      ) : null}
      {blockDraft ? (
        <Modal tone="danger" testid="appt-block-modal" icon={<Icon.warn size={16} />}
          title={t("appointments.detail.blockTitle", "{arg1} · {arg2}", { arg1: t("appointments.detail.block", "Block"), arg2: t(BLOCK_KIND_LABEL_KEY[blockDraft.kind], BLOCK_KIND_LABEL[blockDraft.kind]) })}
          sub={t("appointments.detail.blockSub", "New bookings from this identifier are refused until it is unblocked. Existing appointments are left alone.")}
          onClose={() => setBlockDraft(null)}
          footer={<>
            <Button variant="secondary" onClick={() => setBlockDraft(null)}>{t("common.actions.cancel", "Cancel")}</Button>
            <Button disabled={busy} onClick={() => { void confirmBlock(); }} data-testid="appt-block-confirm">{t("appointments.detail.blockConfirm", "Block")}</Button>
          </>}>
          <div style={fieldBox}>{blockDraft.value}</div>
          <Input label={t("appointments.detail.blockReason", "Reason (optional)")} value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)} testid="appt-block-reason"
            placeholder={t("appointments.detail.blockReasonHint", "Recorded on the block list, e.g. repeated no-shows")} />
        </Modal>
      ) : null}
    </Screen>
  );
}
